import { IIndustryWithPolicyTypes, updateIndustry } from "@/api/industries";
import { getPolicyTypesUnpaginated } from "@/api/policy-types";
import { Button } from "@/components/shared/buttons/Button";
import { Panel } from "@/components/shared/Panel";
import { Edit } from "@mui/icons-material";
import {
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    Stack,
    Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

interface Props {
    industry: IIndustryWithPolicyTypes;
}

export const IndustryPolicyTypes = ({ industry }: Props) => {
    const [isEditing, setIsEditing] = useState(false);
    const [selectedIds, setSelectedIds] = useState<number[]>(
        industry.policy_types.map((pt) => pt.id)
    );
    const queryClient = useQueryClient();

    const { data: allPolicyTypes = [] } = useQuery({
        queryKey: ["policy-types-all"],
        queryFn: getPolicyTypesUnpaginated,
        enabled: isEditing,
    });

    const mutation = useMutation({
        mutationFn: () =>
            updateIndustry(industry.id, { policy_type_ids: selectedIds }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["admin-industry", String(industry.id)],
            });
            queryClient.invalidateQueries({ queryKey: ["admin-industries"] });
            setIsEditing(false);
        },
    });

    const handleToggle = (id: number) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleOpen = () => {
        setSelectedIds(industry.policy_types.map((pt) => pt.id));
        setIsEditing(true);
    };

    return (
        <Panel
            title="Policy Types"
            actions={
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Edit fontSize="small" />}
                    onClick={handleOpen}
                    sx={{
                        color: (theme) => theme.palette.primary.contrastText,
                        borderColor: (theme) =>
                            theme.palette.primary.contrastText,
                        "&:hover": {
                            backgroundColor: (theme) =>
                                theme.palette.action.hover,
                        },
                        "& .MuiSvgIcon-root": {
                            fontSize: "1rem",
                        },
                    }}
                >
                    Edit
                </Button>
            }
        >
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {industry.policy_types.length === 0 ? (
                    <Typography color="text.secondary">
                        No policy types assigned
                    </Typography>
                ) : (
                    industry.policy_types.map((pt) => (
                        <Chip
                            key={pt.id}
                            label={`${pt.name} (${pt.short_name})`}
                        />
                    ))
                )}
            </Stack>

            <Dialog
                open={isEditing}
                onClose={() => setIsEditing(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Edit Policy Types</DialogTitle>
                <DialogContent>
                    <FormGroup>
                        {allPolicyTypes.map((pt) => (
                            <FormControlLabel
                                key={pt.id}
                                control={
                                    <Checkbox
                                        checked={selectedIds.includes(pt.id)}
                                        onChange={() => handleToggle(pt.id)}
                                    />
                                }
                                label={`${pt.name} (${pt.short_name})`}
                            />
                        ))}
                    </FormGroup>
                </DialogContent>
                <DialogActions>
                    <Button
                        type="button"
                        variant="outlined"
                        size="small"
                        onClick={() => setIsEditing(false)}
                        disabled={mutation.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="contained"
                        size="small"
                        onClick={() => mutation.mutate()}
                        loading={mutation.isPending}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Panel>
    );
};
